import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { detailedItineraries } from "../data/itinerary";
import { fetchItineraryBySlug } from "../services/api";
import { Helmet } from "react-helmet-async";
import Breadcrumb from "./Breadcrumb";
import SEO from "./SEO";

const ItineraryDetail = () => {
  const { slug } = useParams();

  /* ── STATE ── */
  const [itineraryDetails, setItineraryDetails] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [selected360Place, setSelected360Place] = useState("");
  const [guide, setGuide] = useState(null);
  const [guideLoading, setGuideLoading] = useState(true);
  const [activeDay, setActiveDay] = useState(1);
  const [relatedItineraries, setRelatedItineraries] = useState([]);

  /* ── Scroll tracker for active day ── */
  useEffect(() => {
    const onScroll = () => {
      const sections = document.querySelectorAll("[data-day]");
      let current = 1;
      sections.forEach((section) => {
        const rect = section.getBoundingClientRect();
        if (rect.top <= window.innerHeight * 0.4) current = Number(section.dataset.day);
      });
      setActiveDay(current);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleShare = async () => {
  const baseUrl = process.env.REACT_APP_SITE_URL;
  const url = `${baseUrl}/itineraries/${slug}`;

  const text = `Check out this ${itineraryDetails.title} on Expeditio ✈️`;

  try {
    if (navigator.share) {
      await navigator.share({
        title: itineraryDetails.title,
        text,
        url,
      });
    } else {
      const encoded = encodeURIComponent(text + " " + url);
      window.open(`https://wa.me/?text=${encoded}`, "_blank");
    }
  } catch (err) {
    console.log("Share failed:", err);
  }
};

  /* ── Load itinerary ── */
  useEffect(() => {
    const loadItinerary = async () => {
      try {
        const staticData = Object.values(detailedItineraries).find((it) => it.slug === slug);
        if (staticData) { setItineraryDetails(staticData); return; }
        const dbData = await fetchItineraryBySlug(slug);
        setItineraryDetails(dbData);
      } catch (err) {
        console.error("Failed to load itinerary:", err);
        setItineraryDetails(null);
        setNotFound(true);
      }
      window.scrollTo({ top: 0, behavior: "smooth" });
    };
    loadItinerary();
  }, [slug]);

  /* ── Fetch local guide ── */
  useEffect(() => {
    if (!itineraryDetails?.location) return;
    const fetchGuide = async () => {
      try {
        const res = await fetch(
          `${process.env.REACT_APP_API_URL}/api/guides?city=${itineraryDetails.location}`
        );
        const data = await res.json();
        setGuide(data?.guide || null);
      } catch {
        setGuide(null);
      } finally {
        setGuideLoading(false);
      }
    };
    fetchGuide();
  }, [itineraryDetails]);

  /* ── Fetch related itineraries ── */
  useEffect(() => {
    fetch(`${process.env.REACT_APP_API_URL}/api/itineraries`)
      .then((r) => (r.ok ? r.json() : []))
      .then((data) => {
        if (!Array.isArray(data)) return;
        const related = data.filter((it) => it.slug !== slug).slice(0, 3);
        setRelatedItineraries(related);
      })
      .catch(() => setRelatedItineraries([]));
  }, [slug]);

  /* ── 360 places ── */
  const highlight360Places =
    itineraryDetails?.highlight360Views ||
    (() => {
      const unique = {};
      (itineraryDetails?.days || []).forEach((day) => {
        (day.places || []).forEach((p) => {
          if (!unique[p]) unique[p] = { label: p.split(",")[0], place: p };
        });
      });
      return Object.values(unique).slice(0, 6);
    })();

  useEffect(() => {
    if (highlight360Places.length > 0) setSelected360Place(highlight360Places[0].place);
  }, [highlight360Places]); // eslint-disable-line

  /* ── Map route ── */
  const openDayInMaps = (day) => {
    if (!day?.places || day.places.length < 2) return;
    const origin      = day.places[0];
    const destination = day.places[day.places.length - 1];
    const waypoints   = day.places.length > 2 ? day.places.slice(1, -1).join("|") : "";
    const url =
      `https://www.google.com/maps/dir/?api=1` +
      `&origin=${encodeURIComponent(origin)}` +
      `&destination=${encodeURIComponent(destination)}` +
      (waypoints ? `&waypoints=${encodeURIComponent(waypoints)}` : "");
    window.open(url, "_blank");
  };

  /* ── Loading ── */
  if (notFound) {
    return (
      <>
        <Helmet>
          <meta name="robots" content="noindex, follow" />
        </Helmet>
        <div className="min-h-[60vh] flex flex-col items-center justify-center gap-4">
          <p className="text-gray-500">Itinerary not found</p>
          <Link
            to="/itineraries"
            className="rounded-full border px-6 py-2 inline-block"
          >
            ← Back
          </Link>
        </div>
      </>
    );
  }

  if (!itineraryDetails) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <p className="text-gray-500 animate-pulse">Loading itinerary...</p>
      </div>
    );
  }

  const safe = {
    highlights: [],
    highlight360Views: [],
    days: [],
    inclusions: [],
    exclusions: [],
    tips: [],
    budget: {},
    ...itineraryDetails,
  };

  /* ── Derive cross-link slugs ── */
  // citySlug: use slug directly (itinerary slug matches city slug)
  const citySlug     = slug;
  const blogSlug     = `${slug}-travel-guide`;
  const cityName     = itineraryDetails.location?.split(",")[0]?.trim() ||
                       slug.split("-").map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");

  return (
    <>
      <SEO 
        title={`${itineraryDetails.title} | ${itineraryDetails.duration} Trip`}
        description={`Plan your ${itineraryDetails.title}. ${itineraryDetails.description}`}
        url={`/itineraries/${slug}`}
        image={itineraryDetails.image}
      />
     <Helmet>
  <meta
    name="keywords"
    content={`${itineraryDetails.title}, ${cityName} itinerary, ${cityName} travel plan, ${itineraryDetails.duration} trip`}
  />

  <script
    type="application/ld+json"
    dangerouslySetInnerHTML={{
      __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "Trip",
        name: itineraryDetails.title,
        description: itineraryDetails.description,
        url: `https://www.expeditio.world/itineraries/${slug}`,
        image: itineraryDetails.image,
        itinerary: {
          "@type": "ItemList",
          itemListElement: (itineraryDetails.days || []).map((day, i) => ({
            "@type": "ListItem",
            position: i + 1,
            name: day.title || `Day ${day.day}`,
          })),
        },
        provider: {
          "@type": "Organization",
          name: "Expeditio",
          url: "https://www.expeditio.world",
        },
      }),
    }}
  />

  <script
    type="application/ld+json"
    dangerouslySetInnerHTML={{
      __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: "https://www.expeditio.world/" },
          { "@type": "ListItem", position: 2, name: "Itineraries", item: "https://www.expeditio.world/itineraries" },
          {
            "@type": "ListItem",
            position: 3,
            name: itineraryDetails.title,
            item: `https://www.expeditio.world/itineraries/${slug}`,
          },
        ],
      }),
    }}
  />
</Helmet>

      <section className="bg-white px-4 pt-24 pb-32">
        <div className="max-w-5xl mx-auto space-y-14 md:space-y-16">

          {/* ── BREADCRUMB ── */}
          <Breadcrumb crumbs={[
            { label: "Home", to: "/" },
            { label: "Itineraries", to: "/itineraries" },
            { label: itineraryDetails.title },
          ]} />

          {/* ── HERO ── */}
          <header className="space-y-5">
            <Link
              to="/itineraries"
              className="text-sm text-gray-500 hover:text-[#5b7c67] hover:underline transition-colors"
            >
              ← Back to Itineraries
            </Link>
            <p className="text-xs uppercase tracking-[0.35em] text-gray-400">Complete Travel Guide</p>

            <div className="relative">
              <button
                  onClick={handleShare}
                  className="absolute top-4 right-4 bg-white/90 backdrop-blur px-4 py-2 rounded-full text-sm font-medium shadow hover:scale-105 transition"
                >
                  🔗 Share
                </button>
              <img
                src={itineraryDetails.image}
                alt={itineraryDetails.title}
                className="w-full h-[380px] md:h-[360px] object-cover rounded-[36px]"
              />
              <div className="absolute bottom-6 left-6 bg-white/90 backdrop-blur rounded-2xl px-6 py-4 shadow-lg max-w-xl">
                <h1 className="text-2xl font-semibold">{itineraryDetails.title}</h1>
                <p className="text-sm text-gray-600 mt-1">{itineraryDetails.description}</p>
              </div>
            </div>
          </header>

          {/* ── CROSS-LINK BAR (City + Blog) ── */}
          <div className="flex flex-wrap gap-3 pt-2">
            <Link
              to={`/cities/${citySlug}`}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#5b7c67]/10 text-[#5b7c67] text-xs font-semibold hover:bg-[#5b7c67]/20 transition-colors"
            >
              🏙 Explore {cityName}
            </Link>
            <Link
              to={`/blogs/${blogSlug}`}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#5b7c67]/10 text-[#5b7c67] text-xs font-semibold hover:bg-[#5b7c67]/20 transition-colors"
            >
              📖 {cityName} travel guide
            </Link>
          </div>

          {/* ── OVERVIEW ── */}
          <section className="bg-white py-10 -mx-4 px-4">
            <div className="max-w-5xl mx-auto space-y-8">
              <h2 className="text-2xl font-semibold">Overview</h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Fact label="Best time"  value={itineraryDetails.bestTime} />
                <Fact label="Duration"   value={itineraryDetails.duration} />
                <Fact label="Difficulty" value={itineraryDetails.difficulty} />
                <Fact label="Budget"     value={itineraryDetails.priceRange} />
              </div>
            </div>
          </section>

          {/* ── HIGHLIGHTS ── */}
          <section>
            <h2 className="text-2xl font-semibold mb-4">Why this trip is special</h2>
            <ul className="space-y-4">
              {itineraryDetails.highlights?.map((h, i) => (
                <li key={i} className="flex gap-3">
                  <span className="text-[#5b7c67] text-lg">✦</span>
                  <p className="text-sm text-gray-700 leading-relaxed">{h}</p>
                </li>
              ))}
            </ul>
          </section>

          {/* ── 360 MAP EXPERIENCE ── */}
          {highlight360Places.length > 0 && (
            <section className="bg-transparent py-14 -mx-4 px-4">
              <div className="max-w-6xl mx-auto space-y-12">
                <div className="space-y-2 text-center">
                  <h2 className="text-3xl font-semibold">Explore on Map (360°)</h2>
                  <p className="text-sm text-gray-500 max-w-xl mx-auto">
                    Preview the key locations from your itinerary before you go.
                  </p>
                </div>
                <div className="flex flex-wrap justify-center gap-3">
                  {highlight360Places.map(({ label, place }) => (
                    <button
                      key={place}
                      onClick={() => setSelected360Place(place)}
                      className={`px-5 py-2 rounded-full text-sm font-medium transition
                        ${selected360Place === place
                          ? "bg-[#5b7c67] text-white"
                          : "bg-white text-[#5b7c67] hover:bg-[#5b7c67]/10"
                        }`}
                    >
                      📍 {label}
                    </button>
                  ))}
                </div>
                <div className="relative rounded-[36px] overflow-hidden shadow-[0_20px_60px_rgba(0,0,0,0.15)]">
                  <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent z-10" />
                  <iframe
                    title={`360 view of ${selected360Place}`}
                    src={`https://www.google.com/maps?q=${encodeURIComponent(selected360Place)}&output=svembed`}
                    className="w-full h-[420px] md:h-[600px]"
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                  />
                </div>
              </div>
            </section>
          )}

          {/* ── DAY-WISE ITINERARY ── */}
          <section className="bg-transparent py-16 -mx-4 px-4">
            <div className="max-w-4xl mx-auto space-y-10">
              <div>
                <h2 className="text-4xl font-semibold">Itinerary</h2>
                <p className="text-sm text-gray-500 mt-1">Follow your journey day by day</p>
              </div>
              <div className="relative">
                <div className="absolute left-4 top-0 bottom-0 w-px bg-gray-200" />
                {safe.days.map((day) => (
                  <div key={day.day} data-day={day.day} className="relative pl-12 pb-6">
                    <div
                      className={`absolute left-2 top-6 w-4 h-4 rounded-full border-2 transition-all duration-300
                        ${activeDay === day.day
                          ? "bg-[#5b7c67] border-[#5b7c67] scale-110"
                          : "bg-white border-gray-300"
                        }`}
                    />
                    <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-3">
                      <div>
                        <p className="text-lg font-semibold text-gray-800">Day {day.day}</p>
                        {day.title && <p className="text-sm text-gray-500">{day.title}</p>}
                      </div>
                      <ul className="space-y-2 text-sm text-gray-700 leading-relaxed">
                        {(day.activities || []).map((a, i) => (
                          <li key={i} className="flex gap-2">
                            <span className="text-[#5b7c67]">•</span>
                            <span>{a}</span>
                          </li>
                        ))}
                      </ul>
                      {day.places?.length > 1 && (
                        <button
                          onClick={() => openDayInMaps(day)}
                          className="text-sm font-medium text-[#5b7c67] hover:text-[#4a6a58]"
                        >
                          🗺 View route →
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* ── LOCAL GUIDE ── */}
          <section className="space-y-6">
            <h2 className="text-2xl font-semibold">Local guide</h2>
            {guideLoading ? (
              <div className="bg-white rounded-2xl border border-gray-200 p-5">
                <p className="text-gray-500 text-sm">Checking availability…</p>
              </div>
            ) : guide ? (
              <div className="bg-white rounded-2xl border border-gray-200 p-5 space-y-3">
                <p className="text-lg font-semibold text-gray-800">{guide.name}</p>
                <p className="text-sm text-gray-700 leading-relaxed">
                  Verified local expert in <strong>{guide.city}</strong>.
                  {guide.languages?.length > 0 && (
                    <> Languages spoken: {guide.languages.join(", ")}.</>
                  )}
                </p>
                {/* External WhatsApp stays as <a> – it's an external link */}
                <a
                  href={`https://wa.me/${guide.phone}`}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-2 text-sm font-medium text-[#5b7c67] hover:text-[#4a6a58]"
                >
                  💬 Contact on WhatsApp →
                </a>
              </div>
            ) : (
              <div className="bg-white rounded-2xl border border-gray-200 p-5 space-y-2">
                <p className="text-sm text-gray-600">No local guide available yet.</p>
                {/* External mailto stays as <a> */}
                <a href="mailto:your@email.com" className="text-sm font-medium text-[#5b7c67] hover:underline">
                  Contact us →
                </a>
              </div>
            )}
          </section>

          {/* ── PRACTICAL INFO ── */}
          <section className="bg-white py-12 -mx-4 px-4">
            <div className="max-w-5xl mx-auto grid sm:grid-cols-3 gap-6 text-sm text-gray-700">
              <div className="bg-white rounded-2xl border border-gray-200 p-6">
                <h3 className="font-semibold mb-4 text-gray-800">Included</h3>
                <ul className="space-y-2">
                  {safe.inclusions.map((item, idx) => (
                    <li key={idx} className="flex gap-2">
                      <span className="text-[#5b7c67]">✓</span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="bg-white rounded-2xl border border-gray-200 p-6">
                <h3 className="font-semibold mb-4 text-gray-800">Not included</h3>
                <ul className="space-y-2">
                  {safe.exclusions.map((item, idx) => (
                    <li key={idx} className="flex gap-2">
                      <span className="text-gray-400">–</span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="bg-white rounded-2xl border border-gray-200 p-6">
                <h3 className="font-semibold mb-4 text-gray-800">Travel tips</h3>
                <ul className="space-y-2">
                  {safe.tips.map((t, idx) => (
                    <li key={idx} className="flex gap-2">
                      <span className="text-[#5b7c67]">•</span>
                      <span>{t}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </section>

          {/* ── BUDGET ── */}
          <section className="space-y-6">
            <h2 className="text-2xl font-semibold">Budget breakdown</h2>
            <div className="space-y-3 text-sm text-gray-700">
              {Object.entries(safe.budget).map(([k, v]) => (
                <div key={k} className="flex justify-between">
                  <span className="capitalize">{k}</span>
                  <span>{v}</span>
                </div>
              ))}
            </div>
          </section>

          {/* ── RELATED ITINERARIES ── */}
          {relatedItineraries.length > 0 && (
            <section className="space-y-5">
              <h2 className="text-2xl font-semibold">More itineraries you'll love</h2>
              <div className="grid sm:grid-cols-3 gap-4">
                {relatedItineraries.map((it) => (
                  <Link
                    key={it._id || it.slug}
                    to={`/itineraries/${it.slug}`}
                    className="block border rounded-2xl p-5 hover:border-[#5b7c67] hover:shadow-md transition-all group"
                  >
                    <div className="text-[10px] uppercase tracking-widest text-gray-400 mb-2">Itinerary</div>
                    <h3 className="text-sm font-semibold text-gray-800 group-hover:text-[#5b7c67] transition-colors line-clamp-2">
                      {it.title}
                    </h3>
                    {it.duration && (
                      <div className="text-xs text-gray-400 mt-1">{it.duration}</div>
                    )}
                    <div className="mt-3 text-xs text-[#5b7c67] font-medium">View plan →</div>
                  </Link>
                ))}
              </div>
            </section>
          )}

          {/* ── BOTTOM CROSS-LINKS (bidirectional loop) ── */}
          <div className="mt-8 pt-8 border-t border-gray-200 flex flex-wrap gap-4">
            <Link
              to={`/cities/${citySlug}`}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#5b7c67]/10 text-[#5b7c67] text-sm font-semibold hover:bg-[#5b7c67]/20 transition-colors"
            >
              🏙 Things to do in {cityName}
            </Link>
            <Link
              to={`/blogs/${blogSlug}`}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#5b7c67]/10 text-[#5b7c67] text-sm font-semibold hover:bg-[#5b7c67]/20 transition-colors"
            >
              📖 Read the {cityName} travel guide
            </Link>
            <Link
              to="/itineraries"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-gray-200 text-gray-600 text-sm font-medium hover:border-[#5b7c67] hover:text-[#5b7c67] transition-colors"
            >
              ← Browse all itineraries
            </Link>
          </div>

        </div>
      </section>
    </>
  );
};

export default ItineraryDetail;

/* ── Fact card ── */
function Fact({ label, value }) {
  return (
    <div className="bg-white rounded-2xl p-4 shadow-sm">
      <div className="text-xs text-gray-500">{label}</div>
      <div className="font-medium text-sm mt-1">{value || "—"}</div>
    </div>
  );
}
